import Card from "@material-ui/core/Card";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CardTitle from "@saleor/components/CardTitle";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import { SearchCustomerRedeemedCoupons_search_edges_node } from "@saleor/searches/types/SearchCustomerRedeemedCoupons";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe, renderCollection } from "../../../misc";

const useStyles = makeStyles(
  {
    textLeft: {
      textAlign: "left"
    }
  },
  { name: "CustomerRedeemedCoupons" }
);

export interface CustomerRedeemedCouponsProps extends FetchMoreProps {
  redeemedCoupons: SearchCustomerRedeemedCoupons_search_edges_node[];
}

const CustomerRedeemedCoupons: React.FC<CustomerRedeemedCouponsProps> = props => {
  const { hasMore, onFetchMore, redeemedCoupons } = props;
  const classes = useStyles(props);

  const scrollableDialogClasses = useScrollableDialogStyle({});
  const intl = useIntl();

  const redeemedCouponsList = redeemedCoupons
    ? redeemedCoupons.map(redeemedCoupon => ({
        ...redeemedCoupon
      }))
    : undefined;

  const formatDate = dateString => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date
      .getDate()
      .toString()
      .padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Redeemed Coupons",
          description: "section header"
        })}
      />
      <div style={{ height: "330px", overflowY: "auto" }}>
        <InfiniteScroll
          pageStart={0}
          loadMore={onFetchMore}
          hasMore={hasMore}
          useWindow={false}
          loader={
            <div
              className={scrollableDialogClasses.loadMoreLoaderContainer}
              key={0}
            >
              <CircularProgress size={16} />
            </div>
          }
          threshold={10}
        >
          <ResponsiveTable key="table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <FormattedMessage
                    defaultMessage="Coupon code"
                    description="coupon code"
                  />
                </TableCell>
                <TableCell colSpan={2}>
                  <FormattedMessage
                    defaultMessage="Date Redeemed"
                    description="coupon redemption date"
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderCollection(
                redeemedCouponsList,
                (redeemedCoupon, index) => (
                  <TableRow
                    key={redeemedCoupon ? redeemedCoupon.id : "skeleton"}
                  >
                    <TableCell id={`couponCodeRow${index}`}>
                      {maybe(() => redeemedCoupon.code) ? (
                        <strong>
                          {redeemedCoupon.coupon.code}-{redeemedCoupon.code}
                        </strong>
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      id={`dateRow${index}`}
                      className={classes.textLeft}
                    >
                      {maybe(() => redeemedCoupon.redeemedAt) ? (
                        formatDate(redeemedCoupon.redeemedAt)
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </ResponsiveTable>
        </InfiniteScroll>
      </div>
    </Card>
  );
};

CustomerRedeemedCoupons.displayName = "CustomerRedeemedCoupons";
export default CustomerRedeemedCoupons;
